import { useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { googleLoginService, signInService } from "../Services/authService";
import { useSelector } from "react-redux";
import { useGoogleLogin } from "@react-oauth/google";
import backgroundImage from "../Assets/login_background.jpg";

const SignIn = () => {
  const currentLanguage = useSelector(
    (state) => state?.global?.current_language
  );

  const [loading, setLoading] = useState(false);
  const [googleUserData, setGoogleUserData] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Error state
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [rootError, setRootError] = useState("");

  const navigate = useNavigate();
  const submitInProgress = useRef(false);

  // Validation functions
  const validateEmail = useCallback((emailValue) => {
    if (!emailValue.trim()) {
      return "Email cannot be blank";
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
      return "Please enter a valid email address";
    }
    return "";
  }, []);

  const validatePassword = useCallback((passwordValue) => {
    if (!passwordValue.trim()) {
      return "Password cannot be blank";
    }
    return "";
  }, []);

  // Clear all errors
  const clearAllErrors = useCallback(() => {
    setEmailError("");
    setPasswordError("");
    setRootError("");
  }, []);

  // Handle email change
  const handleEmailChange = useCallback(
    (e) => {
      const value = e.target.value;
      setEmail(value);
      if (emailError) {
        setEmailError("");
      }
    },
    [emailError]
  );

  // Handle password change
  const handlePasswordChange = useCallback(
    (e) => {
      const value = e.target.value;
      setPassword(value);
      if (passwordError) {
        setPasswordError("");
      }
    },
    [passwordError]
  );

  // Handle form submission - FIXED VERSION
  const handleLogin = useCallback(async (e) => {
    // CRITICAL: Prevent default form submission behavior
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Prevent multiple submissions
    if (submitInProgress.current || loading) {
      return;
    }

    submitInProgress.current = true;
    clearAllErrors();

    // Validate inputs
    const emailErr = validateEmail(email);
    const passwordErr = validatePassword(password);

    if (emailErr || passwordErr) {
      setEmailError(emailErr);
      setPasswordError(passwordErr);
      submitInProgress.current = false;
      return;
    }

    setLoading(true);

    try {
      const data = { email: email.trim(), password: password.trim() };
      
      // CRITICAL: Ensure signInService properly handles errors
      const response = await signInService(data);

      // Check if response exists and has proper structure
      if (response && response.status === "success") {
        if (response?.data?.user?.isDemoExpire) {
          toast.error("Demo account has expired. Please contact the admin.");
          return;
        }

        const userData = response.data;
        toast.success("Successfully logged in!");

        // Navigate based on user type
        if (userData?.user?.type == "0" || userData?.user?.type == "4") {
          navigate("/new-admin-dashboard");
        } else {
          navigate("/");
        }
      } else {
        // Handle error response
        const errorMessage = response?.message || "Invalid email or password. Please try again.";
        toast.error(errorMessage);
        setRootError(errorMessage);
      }
    } catch (error) {
      console.error("Login error:", error);

      // CRITICAL: Properly handle different error types
      let errorMessage = "An unexpected error occurred. Please try again.";

      // Check if it's a network error
      if (!error.response) {
        errorMessage = "Network error. Please check your connection.";
      } else if (error.response?.status === 401) {
        errorMessage = "Invalid email or password.";
      } else if (error.response?.status >= 500) {
        errorMessage = "Server error. Please try again later.";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
      setRootError(errorMessage);
    } finally {
      setLoading(false);
      submitInProgress.current = false;
    }
  }, [
    email,
    password,
    loading,
    validateEmail,
    validatePassword,
    clearAllErrors,
    navigate,
  ]);

  // Handle Enter key press - FIXED
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        handleLogin(e);
      }
    },
    [handleLogin]
  );

  // Handle form submission wrapper
  const handleFormSubmit = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    handleLogin(e);
  }, [handleLogin]);

  const signinWithGoogle = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      if (submitInProgress.current) return;

      setGoogleLoading(true);
      submitInProgress.current = true;

      try {
        const response = await googleLoginService(tokenResponse.access_token);

        if (response?.status === "success") {
          const userData = response?.data;

          if (!userData.phoneNumber || !userData.company) {
            setGoogleUserData(userData);
            toast.info("Please complete your profile.");
          } else {
            toast.success("Logged in successfully!");
          }

          if (userData?.user?.type == "0" || userData?.user?.type == "4") {
            navigate("/new-admin-dashboard");
          } else {
            navigate("/");
          }
        } else {
          const errorMessage =
            response?.message || "Google login failed. Please try again.";
          toast.error(errorMessage);
        }
      } catch (error) {
        console.error("Google login error:", error);
        toast.error("Google login failed. Please try again.");
      } finally {
        setGoogleLoading(false);
        submitInProgress.current = false;
      }
    },
    onError: (error) => {
      console.error("Google login error:", error);
      toast.error("Google login failed. Please try again.");
      setGoogleLoading(false);
      submitInProgress.current = false;
    },
  });

  // Handle password visibility toggle
  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  // Handle Google login click
  const handleGoogleLogin = useCallback(() => {
    if (!googleLoading && !loading && !submitInProgress.current) {
      signinWithGoogle();
    }
  }, [googleLoading, loading, signinWithGoogle]);

  return (
    <div className="flex min-h-screen">
      {/* Left side - Background Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
      <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${backgroundImage})`,
            filter: "brightness(0.7)",
          }}
        ></div>
        <div className="relative w-full max-w-2xl p-12 flex flex-col items-center justify-center">
          <h1 className="text-4xl font-bold text-white mb-2 text-center">
            Log in to manage, approve, and track your scaffolding projects with
            ease.
          </h1>
          <p className="text-3xl font-bold text-gray-300 text-start mb-8"></p>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <Link to="/">
              <img
                src="/new_logo.svg"
                alt="Logo"
                className="h-12 mx-auto mb-6"
              />
            </Link>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              Sign in to your account
            </h2>
            <p className="text-gray-600">
              Don't have an account?{" "}
              <Link to="/signup" className="text-blue-600 hover:text-blue-800">
                Sign up →
              </Link>
            </p>
          </div>

          {/* CRITICAL: Wrap inputs in form element */}
          <form onSubmit={handleFormSubmit} noValidate>
            <div className="space-y-6">
              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  onKeyDown={handleKeyDown}
                  autoComplete="email"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    emailError
                      ? "border-red-300 bg-red-50"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                  placeholder="Enter your email"
                  disabled={loading || googleLoading}
                  tabIndex={1}
                />
                {emailError && (
                  <p className="mt-1 text-sm text-red-600">{emailError}</p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={handlePasswordChange}
                    onKeyDown={handleKeyDown}
                    autoComplete="current-password"
                    className={`w-full px-4 py-2 pr-12 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                      passwordError
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                    placeholder="Enter your password"
                    disabled={loading || googleLoading}
                    tabIndex={2}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none focus:text-gray-700 transition-colors"
                    disabled={loading || googleLoading}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <span className="w-5 h-5 block">👁️</span>
                    ) : (
                      <span className="w-5 h-5 block">👁️‍🗨️</span>
                    )}
                  </button>
                </div>
                {passwordError && (
                  <p className="mt-1 text-sm text-red-600">{passwordError}</p>
                )}
              </div>

              {/* Forgot Password Link */}
              <div className="flex items-center justify-end">
                <Link
                  to="/search-email"
                  className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
                  tabIndex={4}
                >
                  Forgot password?
                </Link>
              </div>

              {/* Submit Buttons */}
              <div className="space-y-4">
                {/* Sign In Button - CRITICAL: type="submit" */}
                <button
                  type="submit"
                  disabled={loading || googleLoading || submitInProgress.current}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  tabIndex={3}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-t-2 border-white rounded-full animate-spin"></div>
                      <span className="ml-2">Signing in...</span>
                    </div>
                  ) : (
                    "Sign in"
                  )}
                </button>

                {/* Google Sign In Button */}
                {!googleUserData && (
                  <button
                    type="button"
                    onClick={handleGoogleLogin}
                    disabled={
                      loading || googleLoading || submitInProgress.current
                    }
                    className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-200"
                    tabIndex={5}
                  >
                    {googleLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="w-5 h-5 border-t-2 border-gray-600 rounded-full animate-spin"></div>
                        <span className="ml-2">Connecting...</span>
                      </div>
                    ) : (
                      <>
                        <span className="w-5 h-5 mr-2">🔍</span>
                        Continue with Google
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* Root Error Display */}
              {rootError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{rootError}</p>
                </div>
              )}
            </div>
          </form>

          {/* Footer Links */}
          <div className="mt-8 border-t border-gray-200 pt-6">
            <div className="flex justify-between text-sm text-gray-500">
              <Link
                to="/privacy"
                className="hover:text-gray-700 transition-colors"
              >
                Privacy Policy
              </Link>
              <span>•</span>
              <Link
                to="/terms"
                className="hover:text-gray-700 transition-colors"
              >
                Terms of Use
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
